import type { FC } from 'react'

import { Input, Select, Toggle } from '@skytech/ui'
import { Button } from '@skytech/ui/Button'
import { useQuery } from '@tanstack/react-query'
import GlobeIcon from 'mdi-react/GlobeIcon'
import TrayArrowDownIcon from 'mdi-react/TrayArrowDownIcon'
import { twJoin } from 'tailwind-merge'

import { Translation } from '@/app/translations/Translation'
import { useTranslation } from '@/app/translations/translations-hooks'
import useUser from '@/hooks/user'
import adminQueries from '@/modules/admin/queries'
import adminResources from '@/modules/admin/resources'
import type { ReportType } from '@/modules/admin/resources'

import { useReportsContext } from './index.context'
import { yearOptions } from './utils'

import type { ReportFilterOptions } from '.'

export const NorwayId = 47

export const ReportFilters: FC<{
  filters: Omit<ReportFilterOptions, 'selectedReportType'> & {
    selectedReportType: ReportType | undefined
  }
  isReportListLoading: boolean
  onSetQueryFilters: (
    filters: Omit<ReportFilterOptions, 'selectedReportType'> & {
      selectedReportType: ReportType | undefined
    }
  ) => void
  onOpen: () => void
}> = ({ filters, isReportListLoading, onSetQueryFilters, onOpen }) => {
  const { count } = useReportsContext()
  const translate = useTranslation()
  const user = useUser()

  const { data: customers } = useQuery(
    adminQueries.adminReportCustomers({ statusInclude: [1] }),
    () => adminResources.getReportCustomers({ statusInclude: [1] })
  )

  const userCustomerId = user?.customerId || user?.customerHeadId
  const userCustomer = customers?.find(customer => customer.id === userCustomerId)
  const isNorwegianUser = userCustomer?.countryId === NorwayId

  const reportTypes: {
    value: ReportType
    label: string
  }[] = filterItems(
    [
      { value: 'consumption', label: translate('Consumption (usage)') },
      { value: 'salary-deduction-usage', label: translate('Salary Deduction Usage') },
      { value: 'accounting', label: translate('Accounting Report') },
      { value: 'tax-advantage', label: translate('Tax Advantage Report') },
      { value: 'ssd-order', label: translate('Salary Deduction Device') },
      { value: 'off-boarding', label: translate('Off Boarding Report') },
      { value: 'onboarding', label: translate('Onboarding Report') },
      { value: 'offboarding', label: translate('Offboarding Report') },
      { value: 'new-number-orders', label: translate('New Number Orders') },
    ],
    isNorwegianUser
  )

  return (
    <div
      className={twJoin(
        'mb-5 flex w-full flex-col flex-wrap items-start gap-4 sm:flex-row sm:items-end md:gap-3 lg:flex-nowrap',
        count > 0 && 'lg:justify-between'
      )}
    >
      <div className="w-full min-w-56 sm:w-80">
        <Select
          id="select-report-type"
          value={filters.selectedReportType}
          placeholder={translate('Select report type')}
          label={<Translation id="Select report type" />}
          options={reportTypes}
          onChange={option => {
            const selectedReportType = option?.value
            if (!selectedReportType) throw Error('No report type has been selected!')
            onSetQueryFilters({ ...filters, selectedReportType: selectedReportType })
          }}
        />
      </div>

      {user?.role !== 'customer_admin' && (
        <div className="w-full min-w-32 sm:w-56">
          <Select
            id="select-customer"
            value={filters.selectedCustomer}
            placeholder="Select company"
            label={<Translation id="Select company" />}
            options={customers?.map(cus => ({ label: cus.name, value: cus.id })) || []}
            onChange={option =>
              onSetQueryFilters({
                ...filters,
                selectedCustomer: option?.value ? Number(option?.value) : undefined,
                isGlobal: false,
              })
            }
            isClearable={true}
          />
        </div>
      )}

      {filters.selectedReportType === 'tax-advantage' ? (
        <div className="w-full min-w-32 sm:w-40">
          <Select
            id="select-year"
            value={filters.year}
            placeholder="Select year"
            label={<Translation id="Select year" />}
            options={yearOptions()}
            onChange={option =>
              onSetQueryFilters({ ...filters, year: option ? Number(option?.value) : '' })
            }
          />
        </div>
      ) : (
        <>
          <Input
            required
            capitalizeLabel
            isFullWidth
            id="fromDate"
            type="date"
            wrapperClassName="sm:min-w-36 w-full sm:w-auto"
            value={filters.fromDate}
            ariaLabel={translate('From Date')}
            label={<Translation id="From Date" />}
            onChange={e => {
              const newFromDate = e.target.value
              const updatedFilters = { ...filters, fromDate: newFromDate }

              // If toDate exists and fromDate is after toDate, set toDate to fromDate
              if (
                filters.toDate &&
                newFromDate &&
                new Date(newFromDate) > new Date(filters.toDate)
              ) {
                updatedFilters.toDate = newFromDate
              }

              onSetQueryFilters(updatedFilters)
            }}
          />

          <Input
            required
            capitalizeLabel
            isFullWidth
            id="toDate"
            type="date"
            wrapperClassName="sm:min-w-36 w-full sm:w-auto"
            value={filters.toDate}
            ariaLabel={translate('To Date')}
            label={<Translation id="To Date" />}
            onChange={e => {
              const newToDate = e.target.value
              const updatedFilters = { ...filters, toDate: newToDate }

              // If fromDate exists and toDate is before fromDate, set fromDate to toDate
              if (
                filters.fromDate &&
                newToDate &&
                new Date(newToDate) < new Date(filters.fromDate)
              ) {
                updatedFilters.fromDate = newToDate
              }

              onSetQueryFilters(updatedFilters)
            }}
          />
        </>
      )}

      {user?.role !== 'customer_admin' && (
        <div className="w-40 min-w-28">
          <Toggle
            id="is-global"
            label={
              <div className="flex items-center gap-1">
                <Translation id="Global" />
                <GlobeIcon className="h-4 w-4" />
              </div>
            }
            checked={filters.isGlobal}
            marginTop={16}
            marginBottom={16}
            onChange={() =>
              onSetQueryFilters({
                ...filters,
                isGlobal: !filters.isGlobal,
                selectedCustomer: !filters.isGlobal ? undefined : filters.selectedCustomer,
              })
            }
          />
        </div>
      )}

      {count > 0 && (
        <div className="flex-none text-right">
          <Button
            Icon={<TrayArrowDownIcon />}
            disabled={isReportListLoading}
            variant="primary"
            onClick={onOpen}
            className="m-0 h-12"
          >
            <Translation id="Download Report" />
          </Button>
        </div>
      )}
    </div>
  )
}

const filterItems = <T extends { value: string }>(
  items: (T | false | null | undefined)[],
  isNorwegianUser: boolean
) => {
  const filteredItems: T[] = []
  items.forEach(item => {
    if (!item) return
    // Hide tax-advantage report for non-Norwegian users
    if (!isNorwegianUser && item['value'] === 'tax-advantage') return
    filteredItems.push(item)
  })

  return filteredItems
}
